import { IconLayer, TextLayer } from "@deck.gl/layers"
import { ADSB_LABEL_MIN_ZOOM, ADSB_DEAD_RECKONING_INTERVAL, TrackedAircraft } from "@nova/constants"
import { useAdsbTracking } from "./useAdsbTracking"
import { useSpidertracksTracking } from "./useSpidertracksTracking"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useMemo } from "react"
import {
    setActivePopup,
    setHoveredAircraftId,
    selectHoveredAircraftId,
    selectActiveAircraftId,
    selectActivePopup,
} from "features/overview/slice"
import { useViewport } from "features/map/GlobalViewState"
import { getAircraftLivePosition } from "./aircraftLivePositionStore"

const formatAltitude = (feet: number) => `${feet.toLocaleString()}'`

const iconSize = 30
const altitudeTextSize = 16
const callsignTextSize = 14
const gap = 12
const textOffset = iconSize / 2 + gap

export const AIRCRAFT_POPUP_DIMENSIONS = {
    width: iconSize * 2,
    height: iconSize + altitudeTextSize + callsignTextSize + gap,
}
export const AIRCRAFT_POPUP_MARGIN = { x: 12, y: 4 }

export const useAircraftTrackingLayers = (enabled: boolean) => {
    const dispatch = useAppDispatch()
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { data: adsbData } = useAdsbTracking(orgId)
    const { data: spidertracksData } = useSpidertracksTracking(orgId)
    const { zoom } = useViewport()
    const labelsVisible = zoom >= ADSB_LABEL_MIN_ZOOM
    const activeAircraftId = useAppSelector(selectActiveAircraftId)
    const hoveredAircraftId = useAppSelector(selectHoveredAircraftId)
    const activePopup = useAppSelector(selectActivePopup)
    const aircraftTrackingLayer = useMemo(() => {
        const combinedData = [...(adsbData ?? []), ...(spidertracksData?.aircraft ?? [])]
        if (!enabled || !combinedData.length) return []
        const labelData = labelsVisible
            ? combinedData
            : combinedData.filter(
                  (a) =>
                      a.id === hoveredAircraftId ||
                      a.id === activeAircraftId ||
                      (activePopup && activePopup.type === "aircraft" && activePopup.id === a.id)
              )
        return [
            new IconLayer<TrackedAircraft>({
                id: "aircraft",
                data: combinedData,
                getIcon: () => ({
                    url: "/images/mma-icon.svg",
                    width: 128,
                    height: 128,
                    mask: true,
                }),
                getColor: () => [39, 187, 255],
                getPosition: (a) => [a.lng, a.lat],
                getAngle: (a) => -a.heading,
                getSize: iconSize,
                pickable: true,
                onHover: ({ object }: { object?: TrackedAircraft }) => {
                    dispatch(setHoveredAircraftId(object?.id ?? null))
                },
                onClick: ({ object }: { object?: TrackedAircraft }) => {
                    if (!object) return
                    dispatch(
                        setActivePopup({
                            id: object.id,
                            type: "aircraft",
                            placementType: "live-point",
                            getPoint: () =>
                                getAircraftLivePosition(object.id) ?? {
                                    lat: object.lat,
                                    lng: object.lng,
                                },
                            pointRefreshRateMs: ADSB_DEAD_RECKONING_INTERVAL,
                            renderedDimensions: AIRCRAFT_POPUP_DIMENSIONS,
                            margin: AIRCRAFT_POPUP_MARGIN,
                        })
                    )
                },
                parameters: {
                    depthCompare: "always",
                },
            }),
            new TextLayer<TrackedAircraft>({
                id: "aircraft-altitude",
                data: labelData,
                getPosition: (a) => [a.lng, a.lat],
                getText: (a) => formatAltitude(a.altitude),
                getSize: altitudeTextSize,
                getColor: [255, 255, 255, 255],
                getPixelOffset: [0, -textOffset],
                getTextAnchor: "middle",
                fontWeight: "bold",
                fontFamily: "IBM Plex Mono, monospace",
                fontSettings: { sdf: true },
                outlineWidth: 4,
                outlineColor: [0, 0, 0, 255],
                pickable: false,
                parameters: {
                    depthCompare: "always",
                },
            }),
            new TextLayer<TrackedAircraft>({
                id: "aircraft-callsign",
                data: labelData,
                getPosition: (a) => [a.lng, a.lat],
                getText: (a) => a.callsign,
                getSize: callsignTextSize,
                getColor: [255, 255, 255, 255],
                getPixelOffset: [0, textOffset],
                getTextAnchor: "middle",
                fontWeight: "bold",
                fontFamily: "IBM Plex Mono, monospace",
                fontSettings: { sdf: true },
                outlineWidth: 4,
                outlineColor: [0, 0, 0, 255],
                pickable: false,
                parameters: {
                    depthCompare: "always",
                },
            }),
        ]
    }, [
        adsbData,
        spidertracksData?.aircraft,
        labelsVisible,
        hoveredAircraftId,
        activeAircraftId,
        activePopup,
        enabled,
        dispatch,
    ])
    return aircraftTrackingLayer
}
